import { autoinject, bindable } from 'aurelia-framework';
import { DecimalsValueConverter } from 'value-converters/decimals';
import { I18N } from 'aurelia-i18n';
import { NetsRepairingDto, RepairingDto } from 'models/statistics/nets';
import { PubSub } from 'lib/event';

type Row = {
  __yearHeader__?: boolean;
  Year?: number;
  NetType?: string;
  Hours?: number | string;
  Count?: number | string;
  AverageHours?: number | string;
  Repairs1To3Msk?: number | string;
  Repairs4To10Msk?: number | string;
  RepairsOver10Msk?: number | string;
};

@autoinject()
export class NetsRepairingDataTable {
  @bindable
  data: NetsRepairingDto[] | null = null;

  protected tableTitle: string = this.t.tr('service.repairTab');

  columns: { key: string; label: string }[] = [
    { key: 'NetType', label: this.t.tr('general.NetType') },
    { key: 'Hours', label: 'Hours' },
    { key: 'Count', label: 'Count' },
    { key: 'AverageHours', label: 'Average Hours' },
    { key: 'Repairs1To3Msk', label: 'Repairs 1–3 Msk' },
    { key: 'Repairs4To10Msk', label: 'Repairs 4–10 Msk' },
    { key: 'RepairsOver10Msk', label: 'Repairs >10 Msk' },
  ];

  rows: Row[] = [];

  constructor(
    private pubsub: PubSub,
    private decimalsValueConverter: DecimalsValueConverter,
    private t: I18N
  ) {
    this.pubsub.sub('i18n:locale:changed', () => this.onLocaleChanged());
  }

  onLocaleChanged() {
    this.tableTitle = this.t.tr('service.repairTab');
    this.columns = [
      { key: 'NetType', label: this.t.tr('general.NetType') },
      { key: 'Hours', label: 'Hours' },
      { key: 'Count', label: 'Count' },
      { key: 'AverageHours', label: 'Average Hours' },
      { key: 'Repairs1To3Msk', label: 'Repairs 1–3 Msk' },
      { key: 'Repairs4To10Msk', label: 'Repairs 4–10 Msk' },
      { key: 'RepairsOver10Msk', label: 'Repairs >10 Msk' },
    ];
    this.createTable();
  }

  dataChanged() {
    this.tableTitle = this.t.tr('service.repairTab');
    this.columns = [
      { key: 'NetType', label: this.t.tr('general.NetType') },
      { key: 'Hours', label: 'Hours' },
      { key: 'Count', label: 'Count' },
      { key: 'AverageHours', label: 'Average Hours' },
      { key: 'Repairs1To3Msk', label: 'Repairs 1–3 Msk' },
      { key: 'Repairs4To10Msk', label: 'Repairs 4–10 Msk' },
      { key: 'RepairsOver10Msk', label: 'Repairs >10 Msk' },
    ];
    this.createTable();
  }

  createTable() {
    const arr: NetsRepairingDto[] = Array.isArray(this.data) ? this.data : [];
    // sort by year in descending order
    arr.sort((a, b) => (b.Year ?? 0) - (a.Year ?? 0));

    const rows: Row[] = [];

    for (const y of arr) {
      if (!y || !Array.isArray(y.RepairingData)) continue;

      // year header row
      rows.push({ __yearHeader__: true, Year: y.Year });

      // sort net types alphabetically
      const nets = [...y.RepairingData].sort((a, b) => (a.NetType ?? '').localeCompare(b.NetType ?? ''));

      for (const n of nets) {
        const rds: RepairingDto[] = Array.isArray(n.RepairingData) ? n.RepairingData : [];

        if (rds.length === 0) {
          rows.push({
            NetType: n.NetType,
            Hours: '',
            Count: '',
            AverageHours: '',
            Repairs1To3Msk: '',
            Repairs4To10Msk: '',
            RepairsOver10Msk: '',
          });
        } else {
          const r = rds[0];
          const c = safeInt(r.Count);
          const h = safeNum(r.Hours);
          const avg = r.AverageHours ?? (c > 0 ? h / c : null);

          const d = r.DamageData;
          rows.push({
            NetType: n.NetType,
            Hours: h || '',
            Count: c || '',
            AverageHours: avg != null ? avg : '',
            Repairs1To3Msk: c > 0 && d ? safeNum(d.Repairs1To3Msk) : '',
            Repairs4To10Msk: c > 0 && d ? safeNum(d.Repairs4To10Msk) : '',
            RepairsOver10Msk: c > 0 && d ? safeNum(d.RepairsOver10Msk) : '',
          });
        }
      }
    }

    this.rows = rows;
  }

  isYearHeader(row: Row): boolean {
    return !!row.__yearHeader__;
  }

  yearLabel(row: Row): string {
    return `Year: ${row.Year}`;
  }

  cell(row: Row, col: keyof Row) {
    return row[col];
  }

  formatCell(v: string | number | null): string {
    if (v == null) return '';
    if (typeof v === 'number' && Number.isFinite(v)) {
      return this.decimalsValueConverter.toView(v, 2);
    }
    return String(v);
  }

  detached() {
    this.pubsub.unsub();
  }
}

function safeNum(x: string | number): number {
  const n = Number(x);
  return Number.isFinite(n) ? n : 0;
}
function safeInt(x: string | number): number {
  const n = parseInt(String(x ?? ''), 10);
  return Number.isFinite(n) ? n : 0;
}
